import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';

import { CacheKey, setJSONToLocalStorage, getJSONFromLocalStorage } from 'utils/localStorage';

enum ActionType {
  ToggleSidebar = 'TOGGLE_SIDEBAR',
  ToggleDarkMode = 'TOGGLE_DARK_MODE',
  SetDarkMode = 'SET_DARK_MODE',
}

type State = {
  isSidebarExpanded: boolean;
  isDarkMode: boolean;
  isModeToggledManually: boolean;
};

const CachedState: Partial<State> = getJSONFromLocalStorage(CacheKey.Settings);

const InitialState: State = {
  isSidebarExpanded: false,
  isDarkMode: false,
  isModeToggledManually: false,
  ...CachedState,
};

interface Action {
  type: ActionType;
  payload?: any;
}

type ContextProps = State & {
  toggleSidebar: () => void;
  toggleDarkMode: () => void;
};

const SettingsContext = createContext({} as ContextProps);

export const useSettings = () => useContext(SettingsContext);

const toggleSidebarAction = (): Action => ({
  type: ActionType.ToggleSidebar,
});

const toggleDarkModeAction = (): Action => ({
  type: ActionType.ToggleDarkMode,
});

const setDarkModeAction = (isDarkMode: boolean): Action => ({
  type: ActionType.SetDarkMode,
  payload: isDarkMode,
});

const reducer = (state: State, action: Action): State => {
  const { type, payload } = action;
  switch (type) {
    case ActionType.ToggleSidebar:
      return {
        ...state,
        isSidebarExpanded: !state.isSidebarExpanded,
      };

    case ActionType.ToggleDarkMode:
      return {
        ...state,
        isDarkMode: !state.isDarkMode,
        isModeToggledManually: true,
      };

    case ActionType.SetDarkMode:
      if (payload !== state.isDarkMode) {
        return {
          ...state,
          isDarkMode: payload,
        };
      }
      break;

    default:
      throw new Error(`Unknown action type for SettingsContext: ${type}`);
  }

  return state;
};

const saveSettings = (state: State) => {
  const { isSidebarExpanded, isDarkMode, isModeToggledManually } = state;
  const cachedState: Partial<State> = {
    isSidebarExpanded,
  };
  if (isModeToggledManually) {
    cachedState.isDarkMode = isDarkMode;
    cachedState.isModeToggledManually = true;
  }
  setJSONToLocalStorage(CacheKey.Settings, cachedState);
};

export const SettingsProvider: React.FC = (props) => {
  const [state, dispatch] = useReducer(reducer, InitialState);

  const setDarkMode = useMemo(
    () => (isDarkMode: boolean) => dispatch(setDarkModeAction(isDarkMode)),
    [dispatch]
  );

  useEffect(() => {
    saveSettings(state);

    const html = window.document.getElementsByTagName('html')[0];
    if (state.isDarkMode) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }, [state]);

  useEffect(() => {
    if (
      typeof CachedState?.isDarkMode === 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      setDarkMode(true);
    }
  }, [setDarkMode]);

  return (
    <SettingsContext.Provider
      {...props}
      value={{
        ...state,
        toggleSidebar: () => dispatch(toggleSidebarAction()),
        toggleDarkMode: () => dispatch(toggleDarkModeAction()),
      }}
    />
  );
};
